/**
 * `.layout-filled` and `.layout-empty` are mutually exclusive on the same element.
 * `.layout-open` and `.layout-closed` are mutually exclusive on the same element.
 *
 * @module
 * @author Jacob Viertel <jv@onscreen.net>
 * @since 3.84.0
 */

import {allAnimationsFinished} from '@acng/frontend-bounty';
import {add, remove, toggle} from '@acng/frontend-bounty/style/element.js';
import {STYLE_ACTIVE, STYLE_INACTIVE} from '../config/style.js';
import {isDefined, isUndefined} from '@acng/frontend-bounty/std/value.js';
import {keyIn} from '@acng/frontend-bounty/object.js';
import {each} from '@acng/frontend-bounty/std/array.js';

/**
 * `.layout-filled` is referencing or containing a *filled* list, which is **not empty**.
 */
export const STYLE_FILLED = 'layout-filled';

/**
 * `.layout-empty` is referencing or containing an *empty* list, which is **not filled**.
 */
export const STYLE_EMPTY = 'layout-empty';

export const STYLE_OPEN = 'layout-open';
export const STYLE_CLOSED = 'layout-closed';
export const STYLE_WUMPER = 'layout-wumper';
export const STYLE_BUSY = 'layout-busy';
export const STYLE_IDLE = 'layout-idle';

export {STYLE_ACTIVE, STYLE_INACTIVE};

export const PREFIX_ACTIVE = 'active-';
export const PREFIX_INACTIVE = 'inactive-';
export const PREFIX_DISABLED = 'disabled-';
export const PREFIX_IDLE = 'idle-';
export const PREFIX_BUSY = 'busy-';

const dominant = {
  [STYLE_OPEN]: STYLE_CLOSED,
  [STYLE_CLOSED]: STYLE_OPEN,
  [STYLE_FILLED]: STYLE_EMPTY,
  [STYLE_EMPTY]: STYLE_FILLED,
  [STYLE_ACTIVE]: STYLE_INACTIVE,
  [STYLE_INACTIVE]: STYLE_ACTIVE,
  [STYLE_BUSY]: STYLE_IDLE,
  [STYLE_IDLE]: STYLE_BUSY,
};

/**
 * @param {Element} element
 * @param {string} className
 * @param {boolean} [force]
 * @returns {boolean}
 */
export const swapClass = (element, className, force) => {
  const enabled = toggle(element, className, isUndefined(force) || force);

  if (keyIn(dominant, className)) {
    toggle(element, dominant[className], !enabled);
  }

  return enabled;
}

/**
 * @param {Element} element
 */
export const wumper = async (element) => {
  add(element, STYLE_WUMPER);

  await allAnimationsFinished(element);
  remove(element, STYLE_WUMPER);
};

/**
 * @param {string[]} positiveList
 * @param {string[]} negativeList
 * @param {string[]} undefinedList
 *
 * @returns {(element: Element, condition: boolean | undefined) => void}
 */
const SwapClass = (positiveList, negativeList, undefinedList) => (element, condition) => {
  each(positiveList, (className) => toggle(element, className, isDefined(condition) && condition));
  each(negativeList, (className) => toggle(element, className, isDefined(condition) && !condition));
  each(undefinedList, (className) => toggle(element, className, isUndefined(condition)));
};

export {SwapClass};
